import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { testimonials } from '@/mocks/testimonials';
import { AnimTiltedCard } from '@readdy/anim/card/tilted-card/react';

export default function TestimonialsSection() {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isPaused, setIsPaused] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mq.matches);
    const handler = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const elements = entry.target.querySelectorAll('.reveal-item');
            elements.forEach((el, i) => {
              setTimeout(() => { el.classList.add('visible'); }, i * 100);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    let animationId: number;
    let scrollPos = 0;

    const animate = () => {
      if (!isPaused && container) {
        scrollPos += 0.6;
        const maxScroll = container.scrollWidth - container.clientWidth;
        if (scrollPos >= maxScroll) scrollPos = 0;
        container.scrollLeft = scrollPos;
      }
      animationId = requestAnimationFrame(animate);
    };

    animationId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationId);
  }, [isPaused]);

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }).map((_, i) => (
      <div key={i} className="w-4 h-4 flex items-center justify-center">
        <i className={`ri-star-fill text-sm ${i < rating ? 'text-primary-400' : 'text-background-300'}`} />
      </div>
    ));
  };

  const doubled = [...testimonials, ...testimonials];

  return (
    <section ref={sectionRef} className="w-full py-16 md:py-24 bg-accent-50/20 overflow-hidden">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="text-center mb-10 md:mb-14">
          <span className="inline-block px-4 py-1.5 text-xs font-medium bg-primary-100 text-primary-700 rounded-full mb-4 tracking-wider uppercase">
            Avis clients
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-foreground-800 mb-4">
            {t('testimonials.title')}
          </h2>
          <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
            {t('testimonials.subtitle')}
          </p>
        </div>
      </div>

      <div
        ref={scrollRef}
        className="flex gap-5 md:gap-6 overflow-x-auto pb-4 px-4 md:px-8 lg:px-12"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {doubled.map((testimonial, index) => (
          <div
            key={`${testimonial.id}-${index}`}
            className="reveal-item opacity-0 translate-y-4 transition-all duration-700 flex-shrink-0 w-[320px] md:w-[400px]"
            style={{ transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)' }}
          >
            {reducedMotion ? (
              <div className="bg-background-50 border border-background-200/60 rounded-2xl p-6 md:p-7 h-full hover:border-primary-200/50 hover:shadow-lg transition-all duration-500">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-0.5">
                    {renderStars(testimonial.rating)}
                  </div>
                  <span className="text-xs text-foreground-400">{testimonial.date}</span>
                </div>

                <div className="w-8 h-8 flex items-center justify-center mb-3">
                  <i className="ri-double-quotes-l text-2xl text-primary-200" />
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-5">
                  {testimonial.text}
                </p>

                <div className="flex items-center gap-3 pt-4 border-t border-background-200/40">
                  <div className="w-11 h-11 rounded-full bg-primary-100 flex items-center justify-center">
                    <span className="text-sm font-medium text-primary-700">
                      {testimonial.name.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800">{testimonial.name}</p>
                    <p className="text-xs text-primary-500">{testimonial.service}</p>
                  </div>
                </div>
              </div>
            ) : (
              <AnimTiltedCard maxTilt={12} strength={0.08}>
                <div className="bg-background-50 border border-background-200/60 rounded-2xl p-6 md:p-7 h-full hover:border-primary-200/50 hover:shadow-lg transition-all duration-500">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-0.5">
                      {renderStars(testimonial.rating)}
                    </div>
                    <span className="text-xs text-foreground-400">{testimonial.date}</span>
                  </div>

                  <div className="w-8 h-8 flex items-center justify-center mb-3">
                    <i className="ri-double-quotes-l text-2xl text-primary-200" />
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-5">
                    {testimonial.text}
                  </p>

                  <div className="flex items-center gap-3 pt-4 border-t border-background-200/40">
                    <div className="w-11 h-11 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="text-sm font-medium text-primary-700">
                        {testimonial.name.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">{testimonial.name}</p>
                      <p className="text-xs text-primary-500">{testimonial.service}</p>
                    </div>
                  </div>
                </div>
              </AnimTiltedCard>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}